import React, { useState } from 'react';
import { Alert } from '@patternfly/react-core';
import { AgentDefinition, SDLC_GROUP_META } from '../types';

interface AgentTileProps {
  agent: AgentDefinition;
  onChatClick: (agent: AgentDefinition) => void;
}

export const AgentTile: React.FC<AgentTileProps> = ({ agent, onChatClick }) => {
  const [configError, setConfigError] = useState<string | null>(null);
  const groupColor = SDLC_GROUP_META[agent.sdlcGroup].color;

  const isMisconfigured =
    (agent.mode === 'portal-chat' && !agent.endpoints?.chatUrl) ||
    (agent.mode === 'external-link' && !agent.externalUrl);

  const handleInternalClick = () => {
    if (isMisconfigured) {
      setConfigError('Agent misconfigured — chat URL missing. Ask the catalog admin to update the agent row.');
      return;
    }
    onChatClick(agent);
  };

  const handleExternalClick = () => {
    if (isMisconfigured) {
      setConfigError('Agent misconfigured — external URL missing. Ask the catalog admin to update the agent row.');
    }
  };

  const badge = () => {
    if (agent.mode === 'portal-chat') return null;
    if (agent.mode === 'external-link') {
      return <div className="agent-tile__badge agent-tile__badge--external">EXTERNAL ↗</div>;
    }
    return <div className="agent-tile__badge agent-tile__badge--planned">COMING SOON</div>;
  };

  const statusLine = () => {
    if (isMisconfigured) return <div className="agent-tile__status agent-tile__status--error">⚠ Misconfigured</div>;
    if (agent.mode === 'portal-chat') return <div className="agent-tile__status agent-tile__status--live">Click to chat</div>;
    if (agent.mode === 'external-link') return <div className="agent-tile__status agent-tile__status--external">Opens in new tab ↗</div>;
    return <div className="agent-tile__status">Coming soon</div>;
  };

  const iconStyle = agent.mode === 'portal-chat'
    ? { background: `linear-gradient(135deg, ${groupColor}33, ${agent.color}55)` }
    : agent.mode === 'external-link'
    ? { background: `linear-gradient(135deg, ${agent.color}33, ${agent.color}55)` }
    : undefined;

  const tileClass = [
    'agent-tile',
    agent.mode === 'portal-chat' && !isMisconfigured ? 'agent-tile--live' : '',
    agent.mode === 'external-link' && !isMisconfigured ? 'agent-tile--external' : '',
    agent.mode === 'planned' ? 'agent-tile--planned' : '',
  ].filter(Boolean).join(' ');

  if (agent.mode === 'external-link' && !isMisconfigured) {
    return (
      <a
        href={agent.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={tileClass}
        aria-label={`Open ${agent.name} in new tab`}
      >
        {badge()}
        <div className="agent-tile__icon" style={iconStyle}><span>{agent.icon}</span></div>
        <div className="agent-tile__name">{agent.name}</div>
        {statusLine()}
      </a>
    );
  }

  return (
    <>
      {configError && (
        <Alert
          variant="warning"
          title={configError}
          isInline
          actionClose={<span style={{ cursor: 'pointer' }} onClick={() => setConfigError(null)}>✕</span>}
          style={{ marginBottom: 8 }}
        />
      )}
      <div
        className={tileClass}
        onClick={agent.mode === 'portal-chat' ? handleInternalClick : agent.mode === 'external-link' ? handleExternalClick : undefined}
        onKeyDown={(e) => {
          if ((agent.mode === 'portal-chat' || agent.mode === 'external-link') && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            agent.mode === 'portal-chat' ? handleInternalClick() : handleExternalClick();
          }
        }}
        role={agent.mode !== 'planned' ? 'button' : undefined}
        tabIndex={agent.mode !== 'planned' ? 0 : undefined}
        aria-label={
          agent.mode === 'portal-chat' ? `Chat with ${agent.name}` :
          agent.mode === 'external-link' ? `Open ${agent.name} in new tab` :
          `${agent.name} — coming soon`
        }
        aria-disabled={agent.mode === 'planned'}
      >
        {badge()}
        <div className="agent-tile__icon" style={iconStyle}><span>{agent.icon}</span></div>
        <div className="agent-tile__name">{agent.name}</div>
        {statusLine()}
      </div>
    </>
  );
};
